import { forgotPasswordInput, PASSWORD_RESET_TTL_SEC } from '@fos/shared/contracts/identity';
import { ArrowLeft, MailCheck } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { errorMessage } from '../../../lib/api';
import { useForgotPassword } from '../../../lib/auth';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { AuthLayout } from '../components/AuthLayout';

const backLink = (
  <Link
    to="/login"
    className="inline-flex items-center gap-1.5 rounded-xs font-medium text-fg-secondary hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
  >
    <ArrowLeft size={16} strokeWidth={1.75} aria-hidden />
    Wróć do logowania
  </Link>
);

/**
 * „Nie pamiętasz hasła?”: link do ustawienia nowego hasła mailem. Ten sam komunikat niezależnie od tego,
 * czy konto istnieje (bez ujawniania kont).
 */
export function ForgotPasswordPage() {
  const location = useLocation();
  const forgot = useForgotPassword();
  const [email, setEmail] = useState(() => (location.state as { email?: string } | null)?.email ?? '');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const parsed = forgotPasswordInput.safeParse({ email });
    if (!parsed.success) {
      setFieldError(
        email.trim() ? (parsed.error.issues[0]?.message ?? 'Nieprawidłowy adres email') : 'Podaj adres email',
      );
      return;
    }
    setFieldError(null);
    try {
      await forgot.mutateAsync(parsed.data);
      setSentTo(parsed.data.email);
    } catch (err) {
      // Serwer odpowiada 200 niezależnie od istnienia konta, więc błąd nie zdradza kont (limit, sieć, awaria).
      setFormError(errorMessage(err));
    }
  };

  if (sentTo) {
    return (
      <AuthLayout title="Sprawdź skrzynkę" footer={backLink}>
        <div className="flex flex-col gap-4">
          <span className="inline-flex size-10 items-center justify-center rounded-full bg-accent-subtle text-accent">
            <MailCheck size={20} strokeWidth={1.75} aria-hidden />
          </span>
          <p className="text-body-s text-fg-secondary" role="status">
            Jeśli konto z adresem <span className="font-medium text-fg">{sentTo}</span> istnieje, wysłaliśmy na nie link
            do ustawienia nowego hasła. Link jest ważny {Math.round(PASSWORD_RESET_TTL_SEC / 60)} minut i działa jeden
            raz.
          </p>
          <p className="text-caption text-fg-muted">
            Nie widzisz wiadomości? Sprawdź folder spam albo wyślij link ponownie za kilka minut.
          </p>
          <Button variant="secondary" fullWidth size="l" onClick={() => setSentTo(null)}>
            Wyślij ponownie
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Nie pamiętasz hasła?"
      description="Podaj email, którym się logujesz. Wyślemy link do ustawienia nowego hasła."
      footer={backLink}
    >
      <form onSubmit={submit} noValidate className="flex flex-col gap-4" aria-label="Reset hasła">
        <Field label="Email" error={fieldError}>
          <Input
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="off"
            spellCheck={false}
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        {formError && <FormMessage>{formError}</FormMessage>}
        <Button type="submit" variant="primary" size="l" fullWidth loading={forgot.isPending} className="mt-1">
          Wyślij link
        </Button>
      </form>
    </AuthLayout>
  );
}
